/**
 * Controlador de Registro Público de Eventos.
 *
 * Este módulo gestiona toda la lógica de negocio para el formulario público
 * de registro de eventos, incluyendo validaciones de campos estándar y
 * personalizados, verificación de disponibilidad, cupos y manejo de errores.
 *
 * @author Eulicer Zapata <eulicer.zapata@iudigital.edu.co>
 * @date [Fecha de creación: 2025-11-12]
 * @updated [Fecha de última modificación: 2025-11-12]
 * @updatedBy Eulicer Zapata <eulicer.zapata@iudigital.edu.co>
 */

import { useState, useEffect, useCallback } from "react";
import { getPublicFormInfo, registerToEvent } from "../services/eventRegistrationService";
import PublicRegistration from "../modules/PublicRegistration";
import CustomTemplate1 from "../templates/CustomTemplate1";

/**
 * Controlador principal de registro público de eventos.
 *
 * Este componente maneja toda la lógica del formulario público de registro,
 * incluyendo la carga de información del evento, validaciones en tiempo real,
 * gestión de campos personalizados y envío de datos al servidor.
 *
 * @returns {JSX.Element} Componente con el formulario de registro público.
 */
export default function PublicRegistrationController() {
  // Obtener slug de la URL
  const slug = window.location.pathname.split("/").pop();
  // Estados del formulario
  const [formData, setFormData] = useState({
    name: "",
    lastname: "",
    number_id: "",
    email: "",
    blood_type: "",
    custom_fields: {},
  });

  const [errors, setErrors] = useState({});

  // Estados de carga y envío
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Estados de la información del evento
  const [eventInfo, setEventInfo] = useState(null);
  const [error, setError] = useState(null);

  // Estados de éxito
  const [success, setSuccess] = useState(false);
  const [registeredData, setRegisteredData] = useState(null);

  /**
   * Carga la información del evento desde el servidor.
   *
   * Este método obtiene la configuración completa del formulario público,
   * incluyendo campos personalizados, límites de registro y disponibilidad.
   *
   * @returns {Promise<void>} No retorna valor, actualiza estados.
   */
  const loadEventInfo = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getPublicFormInfo(slug);
      if (result.status !== false && result.data) {
        setEventInfo(result.data);
      } else {
        setError(result.message || "No se pudo cargar la información del evento");
      }
    } catch (err) {
      setError("Error al cargar la información del evento");
    } finally {
      setLoading(false);
    }
  }, [slug]);

  // Cargar información del evento al montar
  useEffect(() => {
    loadEventInfo();
  }, [loadEventInfo]);

  /**
   * Valida un campo individual en tiempo real.
   *
   * Este método aplica reglas de validación específicas según el tipo
   * de campo (nombre, apellido, documento, email) y actualiza los errores.
   *
   * @param {string} field - Nombre del campo a validar.
   * @param {string} value - Valor actual del campo.
   * @returns {void} No retorna valor, actualiza el estado de errores.
   */
  const validateField = (field, value) => {
    const newErrors = { ...errors };

    switch (field) {
      case "name":
        if (!value.trim()) {
          newErrors.name = "El nombre es requerido";
        } else if (value.trim().length < 2) {
          newErrors.name = "El nombre debe tener al menos 2 caracteres";
        } else if (!/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/.test(value)) {
          newErrors.name = "El nombre solo puede contener letras";
        } else {
          newErrors.name = "";
        }
        break;

      case "lastname":
        if (!value.trim()) {
          newErrors.lastname = "El apellido es requerido";
        } else if (value.trim().length < 2) {
          newErrors.lastname = "El apellido debe tener al menos 2 caracteres";
        } else if (!/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/.test(value)) {
          newErrors.lastname = "El apellido solo puede contener letras";
        } else {
          newErrors.lastname = "";
        }
        break;

      case "number_id":
        if (!value.trim()) {
          newErrors.number_id = "El documento de identidad es requerido";
        } else if (!/^\d+$/.test(value)) {
          newErrors.number_id = "El documento debe contener solo números";
        } else if (value.trim().length < 6) {
          newErrors.number_id = "El documento debe tener al menos 6 dígitos";
        } else {
          newErrors.number_id = "";
        }
        break;

      case "email":
        if (!value.trim()) {
          newErrors.email = "El correo electrónico es requerido";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          newErrors.email = "Ingresa un correo electrónico válido";
        } else {
          newErrors.email = "";
        }
        break;

      default:
        newErrors[field] = "";
        break;
    }

    setErrors(newErrors);
  };

  /**
   * Maneja el cambio de un campo del formulario.
   *
   * Este método actualiza el estado del formulario y ejecuta la validación
   * en tiempo real del campo modificado.
   *
   * @param {string} field - Nombre del campo que cambió.
   * @param {string} value - Nuevo valor del campo.
   * @returns {void} No retorna valor, actualiza el estado del formulario.
   */
  const handleInputChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value,
    });

    // Validar en tiempo real
    validateField(field, value);
  };

  /**
   * Valida todos los campos del formulario antes del envío.
   *
   * Este método ejecuta validaciones completas de campos estándar y
   * personalizados, retornando true solo si todos son válidos.
   *
   * @returns {boolean} True si el formulario es válido, false en caso contrario.
   */
  const validateForm = () => {
    const newErrors = {};

    // Validar nombre
    if (!formData.name.trim()) {
      newErrors.name = "El nombre es requerido";
    } else if (formData.name.trim().length < 2) {
      newErrors.name = "El nombre debe tener al menos 2 caracteres";
    } else if (!/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/.test(formData.name)) {
      newErrors.name = "El nombre solo puede contener letras";
    }

    // Validar apellido
    if (!formData.lastname.trim()) {
      newErrors.lastname = "El apellido es requerido";
    } else if (formData.lastname.trim().length < 2) {
      newErrors.lastname = "El apellido debe tener al menos 2 caracteres";
    } else if (!/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/.test(formData.lastname)) {
      newErrors.lastname = "El apellido solo puede contener letras";
    }

    // Validar documento
    if (!formData.number_id.trim()) {
      newErrors.number_id = "El documento de identidad es requerido";
    } else if (!/^\d+$/.test(formData.number_id)) {
      newErrors.number_id = "El documento debe contener solo números";
    } else if (formData.number_id.trim().length < 6) {
      newErrors.number_id = "El documento debe tener al menos 6 dígitos";
    }

    // Validar email
    if (!formData.email.trim()) {
      newErrors.email = "El correo electrónico es requerido";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Ingresa un correo electrónico válido";
    }

    // Validar tipo de sangre (obligatorio en formularios públicos)
    const validBloodTypes = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];
    if (!formData.blood_type) {
      newErrors.blood_type = "El tipo de sangre es requerido";
    } else if (!validBloodTypes.includes(formData.blood_type)) {
      newErrors.blood_type = "Selecciona un tipo de sangre válido";
    }

    // Validar campos personalizados (estructura mixta: secciones + campos sueltos)
    if (eventInfo?.custom_fields && eventInfo.custom_fields.length > 0) {
      const validateSingleField = (field) => {
        const fieldLabel = field.label || field.name || field.id;
        const rawValue = formData.custom_fields[fieldLabel];
        const value = rawValue === undefined || rawValue === null ? "" : rawValue;
        const error = validateCustomField(field, value);
        if (error) {
          newErrors[`custom_${field.id}`] = error;
        }
      };

      eventInfo.custom_fields.forEach((item) => {
        if (item.fields && Array.isArray(item.fields)) {
          // Es una sección: validar cada campo interno
          item.fields.forEach((field) => validateSingleField(field));
        } else if (item.id && item.type) {
          // Es un campo suelto
          validateSingleField(item);
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Maneja el envío del formulario de registro.
   *
   * Este método valida los datos, los formatea correctamente incluyendo
   * campos personalizados en el formato esperado por el backend, y ejecuta
   * el registro mostrando mensajes de éxito o error.
   *
   * @param {Event} e - Evento del formulario.
   * @returns {Promise<void>} No retorna valor, actualiza estados según resultado.
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setError("Por favor, corrige los errores en el formulario antes de enviar.");
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      // Preparar datos para enviar. blood_type es obligatorio en formularios públicos.
      const dataToSend = {
        name: formData.name.trim(),
        lastname: formData.lastname.trim(),
        number_id: formData.number_id.trim(),
        email: formData.email.trim(),
        blood_type: formData.blood_type,
      };

      // Agregar custom_data y custom_field_* para el backend
      // El backend necesita AMBOS formatos:
      // 1. custom_field_{id} para validación
      // 2. custom_data para almacenamiento
      if (formData.custom_fields && Object.keys(formData.custom_fields).length > 0) {
        const customData = {};

        if (eventInfo?.custom_fields) {
          // Iterar estructura mixta: campos sueltos + secciones
          eventInfo.custom_fields.forEach((item) => {
            const processField = (field, sectionName) => {
              const fieldLabel = field.label || field.name || field.id;
              const value = formData.custom_fields[fieldLabel];

              if (value !== undefined && value !== null && value !== "") {
                const fieldId = field.id.replace(/^field_/, "");
                dataToSend[`custom_field_${fieldId}`] = value;

                const fieldKey = fieldLabel
                  .toLowerCase()
                  .normalize("NFD")
                  .replace(/[\u0300-\u036f]/g, "")
                  .replace(/\s+/g, "_")
                  .replace(/[^\w_]/g, "");

                customData[fieldKey] = {
                  label: field.label,
                  type: field.type,
                  ...(sectionName && { section: sectionName }),
                  value: value,
                };
              }
            };

            if (item.fields && Array.isArray(item.fields)) {
              item.fields.forEach((field) => processField(field, item.name));
            } else if (item.id && item.type) {
              processField(item, null);
            }
          });
        }

        // Agregar custom_data solo si hay datos
        if (Object.keys(customData).length > 0) {
          dataToSend.custom_data = customData;
        }
      }

      const result = await registerToEvent(slug, dataToSend);

      if (result.status !== false && result.data) {
        setSuccess(true);
        setRegisteredData(result.data);
        // Limpiar formulario
        setFormData({
          name: "",
          lastname: "",
          number_id: "",
          email: "",
          blood_type: "",
          custom_fields: {},
        });
      } else {
        setError(result.message || "Error al registrarse en el evento");
      }
    } catch (err) {
      setError("Error al procesar el registro");
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Verifica si el formulario está cerrado.
   *
   * Este método evalúa si el formulario está disponible basándose en el
   * estado del backend y las fechas de inicio y fin configuradas.
   *
   * @returns {boolean} True si el formulario está cerrado, false si está disponible.
   */
  const isFormClosed = () => {
    if (!eventInfo) return false;

    // El backend ya indica si está disponible
    if (eventInfo.is_available === false) {
      return true;
    }

    const now = new Date();
    const startDate = eventInfo.form_start_date ? new Date(eventInfo.form_start_date) : null;
    const endDate = eventInfo.form_end_date ? new Date(eventInfo.form_end_date) : null;

    if (startDate && now < startDate) {
      return true;
    }

    if (endDate && now > endDate) {
      return true;
    }

    return false;
  };

  /**
   * Obtiene el tipo de cierre del formulario y su mensaje.
   *
   * Este método determina la razón específica por la cual el formulario
   * no está disponible (inactivo, no iniciado, expirado) y retorna el
   * mensaje apropiado para mostrar al usuario.
   *
   * @returns {Object} Objeto con type, title y message del cierre.
   */
  const getFormClosureInfo = () => {
    if (!eventInfo) return { type: null, title: "", message: "" };

    const now = new Date();
    const startDate = eventInfo.form_start_date ? new Date(eventInfo.form_start_date) : null;
    const endDate = eventInfo.form_end_date ? new Date(eventInfo.form_end_date) : null;

    // Verificar si está desactivado/inactivo
    if (eventInfo.is_available === false) {
      return {
        type: "inactive",
        title: "Formulario Inactivo",
        message:
          eventInfo.unavailable_reason ||
          "Este formulario se encuentra actualmente inactivo. Por favor, contacta al administrador.",
      };
    }

    // Verificar si aún no ha iniciado
    if (startDate && now < startDate) {
      return {
        type: "not_started",
        title: "Formulario No Disponible",
        message: `El periodo de registro aún no ha iniciado. Estará disponible a partir del ${startDate.toLocaleDateString(
          "es-CO",
          { year: "numeric", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit" }
        )}.`,
      };
    }

    // Verificar si ya expiró
    if (endDate && now > endDate) {
      return {
        type: "expired",
        title: "Formulario Cerrado",
        message:
          eventInfo.unavailable_reason ||
          `El periodo de registro finalizó el ${endDate.toLocaleDateString("es-CO", {
            year: "numeric",
            month: "long",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })}`,
      };
    }

    return { type: null, title: "", message: "" };
  };

  /**
   * Verifica si hay cupos disponibles.
   *
   * Este método compara el número de registros actuales contra el límite
   * máximo configurado (si existe).
   *
   * @returns {boolean} True si hay cupos disponibles, false si está lleno.
   */
  const hasAvailableSlots = () => {
    if (!eventInfo) return true;
    if (!eventInfo.max_registrations) return true; // Sin límite

    return eventInfo.current_registrations < eventInfo.max_registrations;
  };

  /**
   * Limpia el mensaje de error.
   *
   * Este método resetea el estado de error a null para ocultar
   * alertas de error en la interfaz.
   *
   * @returns {void} No retorna valor, actualiza el estado.
   */
  const clearError = () => {
    setError(null);
  };

  /**
   * Valida un campo personalizado según su tipo.
   *
   * Este método aplica reglas de validación específicas para campos
   * personalizados (numeric, alphabetic, alphanumeric, select) según
   * la configuración del campo.
   *
   * @param {Object} field - Configuración del campo personalizado.
   * @param {string} field.id - ID único del campo.
   * @param {string} field.label - Etiqueta del campo.
   * @param {string} field.type - Tipo del campo (numeric, alphabetic, etc).
   * @param {boolean} field.required - Si el campo es obligatorio.
   * @param {Array} [field.options] - Opciones para campos tipo select.
   * @param {string} value - Valor actual del campo.
   * @returns {string} Mensaje de error si la validación falla, cadena vacía si es válido.
   */
  const validateCustomField = (field, value) => {
    // Obtener el nombre del campo (puede ser 'label' o 'name' según el backend)
    const fieldName = field.label || field.name || `Campo ${field.id}`;

    // Para checklist, manejar como array
    if (field.type === "checklist") {
      if (field.required) {
        if (!value || (Array.isArray(value) && value.length === 0)) {
          return `${fieldName} es requerido. Debe seleccionar al menos una opción`;
        }
      }
      if (Array.isArray(value) && field.options) {
        const invalidOptions = value.filter((v) => !field.options.includes(v));
        if (invalidOptions.length > 0) {
          return `Opciones no válidas para ${fieldName}`;
        }
      }
      return "";
    }

    // Normalizar a string para el resto de validaciones (evita errores si llega
    // un valor que no es texto, p. ej. un array o un número).
    const stringValue = typeof value === "string" ? value : value == null ? "" : String(value);

    if (!stringValue || stringValue.trim() === "") {
      return field.required ? `${fieldName} es requerido` : "";
    }

    const trimmedValue = stringValue.trim();

    switch (field.type) {
      case "numeric":
        if (!/^\d+$/.test(trimmedValue)) {
          return `${fieldName} debe contener solo números`;
        }
        break;

      case "alphabetic":
        if (!/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/.test(trimmedValue)) {
          return `${fieldName} debe contener solo letras`;
        }
        break;

      case "alphanumeric":
        if (!/^[a-zA-Z0-9áéíóúÁÉÍÓÚñÑ\s]+$/.test(trimmedValue)) {
          return `${fieldName} debe contener solo letras y números`;
        }
        break;

      case "select":
        if (field.options && !field.options.includes(trimmedValue)) {
          return `Selecciona una opción válida para ${fieldName}`;
        }
        break;

      case "checklist":
        // Para checklist, el value será un array
        if (field.required) {
          if (!value || (Array.isArray(value) && value.length === 0)) {
            return `${fieldName} es requerido. Debe seleccionar al menos una opción`;
          }
        }
        if (Array.isArray(value) && field.options) {
          const invalidOptions = value.filter((v) => !field.options.includes(v));
          if (invalidOptions.length > 0) {
            return `Opciones no válidas para ${fieldName}`;
          }
        }
        break;

      default:
        break;
    }

    return "";
  };

  /**
   * Maneja el cambio de un campo personalizado.
   *
   * Este método actualiza el estado del formulario con el nuevo valor del
   * campo personalizado y ejecuta su validación en tiempo real.
   *
   * @param {string} fieldId - ID del campo personalizado que cambió.
   * @param {string} value - Nuevo valor del campo.
   * @returns {void} No retorna valor, actualiza estados.
   */
  const handleCustomFieldChange = (fieldId, value) => {
    // Buscar el campo en eventInfo dentro de la estructura mixta
    let field = null;
    if (eventInfo?.custom_fields) {
      for (const item of eventInfo.custom_fields) {
        if (item.fields && Array.isArray(item.fields)) {
          field = item.fields.find((f) => f.id === fieldId);
          if (field) break;
        } else if (item.id === fieldId) {
          field = item;
          break;
        }
      }
    }
    const fieldLabel = field?.label || field?.name || fieldId;

    setFormData({
      ...formData,
      custom_fields: {
        ...formData.custom_fields,
        [fieldLabel]: value, // Usar el label como key en lugar del id
      },
    });

    // Validar el campo personalizado
    if (field) {
      const error = validateCustomField(field, value);
      setErrors({
        ...errors,
        [`custom_${fieldId}`]: error, // Mantener el id para los errores en la UI
      });
    }
  };

  /**
   * Valida todos los campos personalizados del formulario.
   *
   * Este método ejecuta la validación completa de todos los campos
   * personalizados configurados en el evento.
   *
   * @returns {boolean} True si todos los campos personalizados son válidos.
   */
  const validateCustomFields = () => {
    if (!eventInfo?.custom_fields || eventInfo.custom_fields.length === 0) {
      return true;
    }

    const customErrors = {};
    let isValid = true;

    // Iterar estructura mixta: campos sueltos + secciones
    eventInfo.custom_fields.forEach((item) => {
      if (item.fields && Array.isArray(item.fields)) {
        // Es una sección
        item.fields.forEach((field) => {
          const value = formData.custom_fields[field.id] || "";
          const error = validateCustomField(field, value);
          if (error) {
            customErrors[`custom_${field.id}`] = error;
            isValid = false;
          }
        });
      } else if (item.id && item.type) {
        // Es un campo suelto (legacy)
        const value = formData.custom_fields[item.id] || "";
        const error = validateCustomField(item, value);
        if (error) {
          customErrors[`custom_${item.id}`] = error;
          isValid = false;
        }
      }
    });

    setErrors({
      ...errors,
      ...customErrors,
    });

    return isValid;
  };

  /**
   * Formatea un rango de fechas del evento en una sola línea legible.
   *
   * Este método genera un formato optimizado según si las fechas son
   * del mismo día, mismo mes o diferente mes/año.
   *
   * Ejemplos:
   * - Mismo día: "21 nov 2025, 08:00 - 17:00"
   * - Mismo mes, días distintos: "21 - 22 nov 2025, 08:00 - 17:00"
   * - Diferente mes o año: "30 nov 2025 - 02 dic 2025, 08:00 - 17:00"
   *
   * @param {string} startStr - Fecha de inicio en formato ISO.
   * @param {string} endStr - Fecha de fin en formato ISO.
   * @returns {string} Rango de fechas formateado con fecha y hora.
   */
  const formatEventDateRange = (startStr, endStr) => {
    if (!startStr && !endStr) return "Fecha no definida";

    const start = startStr ? new Date(startStr) : null;
    const end = endStr ? new Date(endStr) : null;

    const optsDate = { year: "numeric", month: "short", day: "numeric" };
    const optsTime = { hour: "2-digit", minute: "2-digit" };

    if (!start) {
      return end
        ? `Hasta ${end.toLocaleDateString("es-CO", optsDate)}, ${end.toLocaleTimeString(
            "es-CO",
            optsTime
          )}`
        : "Fecha no definida";
    }

    if (!end) {
      return `Desde ${start.toLocaleDateString("es-CO", optsDate)}, ${start.toLocaleTimeString(
        "es-CO",
        optsTime
      )}`;
    }

    // Mismo día
    if (
      start.getFullYear() === end.getFullYear() &&
      start.getMonth() === end.getMonth() &&
      start.getDate() === end.getDate()
    ) {
      return `${start.toLocaleDateString("es-CO", optsDate)}, ${start.toLocaleTimeString(
        "es-CO",
        optsTime
      )} - ${end.toLocaleTimeString("es-CO", optsTime)}`;
    }

    // Mismo mes y año, días distintos -> mostrar "21 - 22 nov 2025"
    if (start.getFullYear() === end.getFullYear() && start.getMonth() === end.getMonth()) {
      const startDay = start.getDate();
      const endDay = end.getDate();
      const monthYear = start.toLocaleDateString("es-CO", { year: "numeric", month: "short" });
      return `${startDay} - ${endDay} ${monthYear}, ${start.toLocaleTimeString(
        "es-CO",
        optsTime
      )} - ${end.toLocaleTimeString("es-CO", optsTime)}`;
    }

    // Diferente mes o año
    return `${start.toLocaleDateString("es-CO", optsDate)} - ${end.toLocaleDateString(
      "es-CO",
      optsDate
    )}, ${start.toLocaleTimeString("es-CO", optsTime)} - ${end.toLocaleTimeString(
      "es-CO",
      optsTime
    )}`;
  };

  /**
   * Devuelve sólo la parte de fecha (mes en nombre completo) para un rango
   */
  const formatEventDateRangeDate = (startStr, endStr) => {
    if (!startStr && !endStr) return "Fecha no definida";
    const start = startStr ? new Date(startStr) : null;
    const end = endStr ? new Date(endStr) : null;
    const optsDateLong = { year: "numeric", month: "long", day: "numeric" };

    if (!start) return end ? end.toLocaleDateString("es-CO", optsDateLong) : "Fecha no definida";
    if (!end) return start.toLocaleDateString("es-CO", optsDateLong);

    // Mismo día completo (año, mes y día) -> mostrar "14 de noviembre de 2025"
    if (
      start.getFullYear() === end.getFullYear() &&
      start.getMonth() === end.getMonth() &&
      start.getDate() === end.getDate()
    ) {
      return start.toLocaleDateString("es-CO", optsDateLong);
    }

    // Mismo mes y año -> mostrar "21 - 22 de noviembre de 2025"
    if (start.getFullYear() === end.getFullYear() && start.getMonth() === end.getMonth()) {
      const startDay = start.getDate();
      const endDay = end.getDate();
      const monthYear = start.toLocaleDateString("es-CO", { year: "numeric", month: "long" });
      return `${startDay} - ${endDay} de ${monthYear}`;
    }

    // Diferente mes o año
    return `${start.toLocaleDateString("es-CO", optsDateLong)} - ${end.toLocaleDateString(
      "es-CO",
      optsDateLong
    )}`;
  };

  /**
   * Devuelve sólo la parte de tiempo para el rango: "08:00 - 17:00"
   */
  const formatEventDateRangeTime = (startStr, endStr) => {
    if (!startStr && !endStr) return "Hora no definida";
    const start = startStr ? new Date(startStr) : null;
    const end = endStr ? new Date(endStr) : null;
    const optsTime = { hour: "2-digit", minute: "2-digit" };

    if (!start) return end ? end.toLocaleTimeString("es-CO", optsTime) : "Hora no definida";
    if (!end) return start.toLocaleTimeString("es-CO", optsTime);

    return `${start.toLocaleTimeString("es-CO", optsTime)} - ${end.toLocaleTimeString(
      "es-CO",
      optsTime
    )}`;
  };

  // Renderizar componente de registro público
  /**
   * Reinicia el formulario a su estado inicial (todos los campos vacíos).
   *
   * Se usa al "Volver a la página de inicio" desde la pantalla de confirmación,
   * para iniciar un nuevo registro desde la primera sección.
   *
   * @returns {void}
   */
  const resetForm = () => {
    setFormData({
      name: "",
      lastname: "",
      number_id: "",
      email: "",
      blood_type: "",
      custom_fields: {},
    });
    setErrors({});
    setError(null);
    setSuccess(false);
    setRegisteredData(null);
  };

  // Props comunes para todos los templates
  const templateProps = {
    formData,
    errors,
    loading,
    submitting,
    eventInfo,
    error,
    success,
    registeredData,
    handleInputChange,
    handleSubmit,
    isFormClosed,
    getFormClosureInfo,
    hasAvailableSlots,
    clearError,
    handleCustomFieldChange,
    validateCustomFields,
    formatEventDateRange,
    formatEventDateRangeDate,
    formatEventDateRangeTime,
    resetForm,
  };

  // Seleccionar template según configuración del formulario
  const template = eventInfo?.template || "default";

  if (template === "custom_1") {
    return <CustomTemplate1 {...templateProps} />;
  }

  return <PublicRegistration {...templateProps} />;
}
